import { makeAutoObservable, reaction } from 'mobx';
import { waitUntil } from '../../utils/WaitUntil';
import {
	deleteFaceMemoryPhoto,
	postFaceMemoryPhoto,
} from '../../services/apis/FaceMemoryPhotoApi';
import { editImage, faceDetection } from '../../services/apis/BirthdayApi';
import { queryClient } from '../../hooks/queries/queryClient';

class ContentV2Store {
	rootStore;

	isConnect = false;
	isStart = false;

	downloadProgress = 0;

	sequenceIndex = -1;
	sceneIndex = -1;
	defaultIndex = -1;

	conditions = [];
	onConditionsUpdated = null;

	faces = [];
	processedImageFaces = [];

	constructor(root) {
		makeAutoObservable(this);
		this.rootStore = root;
		this.clear();

		reaction(
			() => this.conditions.map((c) => JSON.stringify(c)),
			() => {
				console.log(
					'[MobX] Conditions changed:',
					this.conditions,
					this.onConditionsUpdated,
				);
				this.onConditionsUpdated?.(this.conditions);
			},
		);
	}

	setIsStart(value = !this.isStart) {
		this.isStart = value;
	}

	getIsReady() {
		return (
			this.isConnect && !this.isStart && this.rootStore.likuStore.thisLiku?.uuid
		);
	}

	setIsConnect(data) {
		this.isConnect = data;
	}

	setOnConditionsUpdated(handler) {
		this.onConditionsUpdated = handler;

		if (this.conditions.length > 0) {
			handler(this.conditions);
		}
	}

	async checkLikuConnect() {
		const liku = this.rootStore.likuStore.thisLiku;

		if (!liku) return;

		await waitUntil(() => !!this.rootStore.likuStore.thisLiku?.language, 100);

		const language = this.rootStore.likuStore.thisLiku.language;

		if (language !== 'ko') {
			await new Promise((resolve) => {
				this.rootStore.mqttStore.likuMqtt.setLanguage('ko', resolve);
			});
		}

		this.rootStore.mqttStore.playerMqtt.setContentsConnect(() => {
			this.setIsConnect(true);
		});
	}

	getChildrenByParentId(list, parentId) {
		return list?.filter((item) => item.parentId === parentId);
	}

	getCurrentSequence(flow) {
		return flow?.sequence?.[this.sequenceIndex];
	}

	getCurrentScene(flow) {
		const sequence = this.getCurrentSequence(flow);
		const sceneList = this.getChildrenByParentId(flow?.scene, sequence?.id);
		return sceneList?.[this.sceneIndex];
	}

	getCurrentDefault(flow) {
		const scene = this.getCurrentScene(flow);
		const defaultList = this.getChildrenByParentId(flow?.default, scene?.id);
		return defaultList?.[this.defaultIndex];
	}

	getNextSequenceIndex(contents, sequenceNode, sceneNode, defaultNode) {
		const edges = contents.flow.edge.filter(
			(edge) => edge.source === sequenceNode.id,
		);
		console.log('getNextSequenceIndex edges', edges);

		if (edges.length === 0) return null;

		const conditionalEdge = edges.find((edge) => edge.data.length > 0);

		if (conditionalEdge) {
			const conditionEdge = this.isConditionMet(edges);

			// 'retry' 반환 시 현재 위치를 다시 재생하기 위해 현재 sequence index 반환
			if (conditionEdge === 'retry') {
				console.log(
					'🔄 [getNextSequenceIndex] 다시 듣기 모드 - 현재 sequence 반복',
				);
				// condition을 초기화하여 다시 들을 수 있도록 준비
				this.setConditions();
				return 'retry';
			}

			if (conditionEdge) {
				this.setConditions();
				return contents.flow.sequence.findIndex(
					(sequence) => sequence.id === conditionEdge.target,
				);
			}
			return 'condition';
		} else {
			return contents.flow.sequence.findIndex(
				(sequence) => sequence.id === edges[0].target,
			);
		}
	}

	isConditionMet(edges) {
		const randomEdges = edges.filter((edge) =>
			edge.data.some((condition) => condition.type.toLowerCase() === 'random'),
		);
		if (randomEdges.length > 0) {
			const randomIndex = Math.floor(Math.random() * randomEdges.length);
			return randomEdges[randomIndex];
		}

		const userCountEdges = edges.filter((edge) =>
			edge.data.some(
				(condition) => condition.type.toLowerCase() === 'usercount',
			),
		);
		console.log('userCountEdges', userCountEdges);
		console.log('edges', edges);
		if (userCountEdges.length > 0) {
			const findEdge = userCountEdges.find((edge) =>
				edge.data.some(
					(condition) => condition.value === this.faces.length.toString(),
				),
			);
			console.log('findEdge', findEdge);
			return findEdge;
		}

		// Sound 타입 특별 처리: transcript 텍스트가 edge value를 포함하는지 확인
		const soundEdges = edges.filter((edge) =>
			edge.data.some((condition) => condition.type.toLowerCase() === 'sound'),
		);

		if (soundEdges.length > 0) {
			// conditions에서 Sound 타입 찾기
			const soundCondition = this.conditions.find(
				(con) => con.type.toLowerCase() === 'sound',
			);

			if (soundCondition) {
				const transcriptText = soundCondition.value.toLowerCase();

				// transcript가 edge의 value를 포함하는지 확인
				for (const edge of soundEdges) {
					const soundData = edge.data.find(
						(condition) => condition.type.toLowerCase() === 'sound',
					);
					const edgeValue = soundData.value.toLowerCase();
					console.log('🎤 [isConditionMet] checking edge value:', edgeValue);

					// transcript에 edge value가 포함되어 있으면 매칭
					if (transcriptText.includes(edgeValue)) {
						console.log(
							'✅ [isConditionMet] Sound edge 매칭 성공:',
							edge,
							`(transcript: "${transcriptText}" includes "${edgeValue}")`,
						);
						return edge;
					}
				}

				// 매칭되는 edge가 없으면 'retry' 반환 (다시 듣기 모드)
				console.log(
					'❌ [isConditionMet] 매칭되는 Sound edge 없음. transcript:',
					transcriptText,
					'→ 다시 듣기 모드',
				);
				return 'retry';
			}
		}

		for (let edge of edges) {
			const isMetEdge = edge.data.every((condition) => {
				const result = this.conditions.some((con) => {
					return (
						con.type.toLowerCase() === condition.type.toLowerCase() &&
						con.value === condition.value
					);
				});
				console.log('result', result);

				return result;
			});

			if (isMetEdge) return edge;
		}

		return false;
	}

	setConditions(condition) {
		console.log('setConditions', condition);
		if (condition) {
			this.conditions.push(condition);
		} else {
			this.conditions = [];
		}
	}

	setSequenceIndex(value = this.sequenceIndex + 1) {
		this.sequenceIndex = value;
	}

	setSceneIndex(value = this.sceneIndex + 1) {
		this.sceneIndex = value;
	}

	setDefaultIndex(value = this.defaultIndex + 1) {
		this.defaultIndex = value;
	}

	getSequenceScenes(contents, sequenceIndex) {
		if (!sequenceIndex) sequenceIndex = this.sequenceIndex;
		const sequenceId = contents?.flow.sequence?.[sequenceIndex]?.id;
		const scenes = contents?.flow.scene.filter(
			(node) => node.parentId === sequenceId,
		);
		return scenes?.[this.sceneIndex];
	}

	setDownloadProgress(value) {
		this.downloadProgress = value;
	}

	setFaces(value) {
		console.log('setFaces value', value);
		this.faces = value;
	}

	async deleteFaceMemory(face) {
		const liku = this.rootStore.likuStore.thisLiku?.serial;

		const index = this.faces.findIndex((p) => p.path === face.path);
		if (index !== -1) this.faces.splice(index, 1);

		if (!('bucket' in face)) {
			await deleteFaceMemoryPhoto(liku, face.id);
		}
	}

	async handleCompleteSelection(profileUsers, saveToDb = true) {
		const allPhotos = [...this.faces, ...profileUsers];

		// 1. 기존 URL 정리 (메모리 누수 방지)
		if (this.processedImageFaces?.length > 0) {
			this.processedImageFaces.forEach((url) => URL.revokeObjectURL(url));
		}
		this.processedImageFaces = [];

		// 2. Blob을 가져와 크기 조절
		const facesBlobs = await Promise.all(
			this.faces.map((photo) => this.fetchAndResize(photo)),
		);
		const profileUsersBlobs = await Promise.all(
			profileUsers.map((photo) => this.fetchAndResize(photo)),
		);
		const allBlobs = [...facesBlobs, ...profileUsersBlobs];

		// 유효성 검사: Blob 가져오기 실패 시 중단
		if (allBlobs.some((blob) => !blob)) {
			console.error('Failed to fetch some photos. Aborting.');
			return false;
		}

		// 3. 얼굴 인식 및 편집을 비동기적으로 수행하고 결과 저장
		const editedFaceBlobs = await Promise.all(
			allBlobs.map((blob) => this.convertAndDetectFace(blob)),
		);

		this.processedImageFaces = editedFaceBlobs
			.filter((blob) => blob)
			.map((blob) => URL.createObjectURL(blob));

		// 4. DB 동기화
		if (saveToDb) {
			await this.applyToDB(profileUsersBlobs);
		}
		// 5. faces 상태 업데이트
		console.log('allPhotos', allPhotos);
		this.setFaces(allPhotos);

		return true;
	}

	async fetchAndResize(photo) {
		try {
			const response = await fetch(photo.path, {
				method: 'GET',
				mode: 'cors',
				cache: 'no-cache',
			});
			if (!response.ok) {
				throw new Error(`HTTP ${response.status}: ${response.statusText}`);
			}
			console.log('fetchAndResize response', response);
			const blob = await response.blob();
			return await this.resizeToMaxSize(blob);
		} catch (error) {
			console.error('Fetch failed:', error.message);
			return null;
		}
	}

	async convertAndDetectFace(blob) {
		try {
			const file = new File([blob], 'faceImage.png', { type: 'image/png' });
			const { data: faces } = await faceDetection(file);
			if (!faces || faces.length === 0) {
				throw new Error('No faces detected');
			}
			const biggestFace = faces.reduce((prev, current) => {
				const prevSize = prev.BoundingBox.Width * prev.BoundingBox.Height;
				const currSize = current.BoundingBox.Width * current.BoundingBox.Height;
				return currSize > prevSize ? current : prev;
			}, faces[0]);
			const imageURL = URL.createObjectURL(file);
			console.log('imageURL', imageURL);
			const editedBlob = await editImage(imageURL, biggestFace);
			URL.revokeObjectURL(imageURL);
			return editedBlob;
		} catch (error) {
			console.error('Face detection or editing failed:', error);
			return null;
		}
	}

	async applyToDB(originalBlobs) {
		const liku = this.rootStore.likuStore.thisLiku?.serial;
		if (!liku) return;

		await Promise.all(
			originalBlobs.map(async (blob) => {
				try {
					const formData = new FormData();
					formData.append('file', blob);
					return await postFaceMemoryPhoto(liku, formData);
				} catch (err) {
					console.error('Failed to upload photo:', err);
				}
			}),
		);
	}

	resizeToMaxSize(blob, maxBytes = 27880, minQuality = 0.1) {
		return new Promise((resolve, reject) => {
			const imageURL = URL.createObjectURL(blob);
			const image = new Image();
			image.onload = async () => {
				try {
					URL.revokeObjectURL(imageURL);
					const canvas = document.createElement('canvas');
					canvas.width = image.width;
					canvas.height = image.height;
					const ctx = canvas.getContext('2d');
					let quality = 0.95;
					let resizedBlob = null;
					while (quality >= minQuality) {
						ctx.clearRect(0, 0, canvas.width, canvas.height);
						ctx.drawImage(image, 0, 0);
						resizedBlob = await new Promise((res) =>
							canvas.toBlob(res, 'image/jpeg', quality),
						);
						if (resizedBlob.size <= maxBytes) {
							resolve(resizedBlob);
							return;
						}
						quality -= 0.05;
					}
					resolve(resizedBlob);
				} catch (error) {
					reject(error);
				}
			};
			image.onerror = (err) => {
				URL.revokeObjectURL(imageURL);
				reject(err);
			};
			image.src = imageURL;
		});
	}

	clear(isAll = true) {
		if (isAll) {
			this.isConnect = false;
			this.onConditionsUpdated = null;
		}
		this.isStart = false;

		this.downloadProgress = 0;

		this.sequenceIndex = -1;
		this.sceneIndex = -1;
		this.defaultIndex = -1;

		this.conditions = [];
	}
}

export default ContentV2Store;
